import { AbstractControl, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

export enum PasswordErrorKeys {
  passwordLength = 'PASSWORD_LENGTH',
  passwordUpper = 'PASSWORD_UPPER',
  passwordLower = 'PASSWORD_LOWER',
  passwordSpecial = 'PASSWORD_SPECIAL',
  passwordNumber = 'PASSWORD_NUMBER',
  passwordMatch = 'PASSWORD_MATCH'
}

/**
 * Function to fetch password error messages dynamically from ngx-translate
 */
export function getPasswordErrorMessages(translate: TranslateService): Promise<{ [key: string]: string }> {
  return new Promise((resolve) => {
    translate.get([
      'PASSWORD_VALIDATIONS.PASSWORD_LENGTH',
      'PASSWORD_VALIDATIONS.PASSWORD_UPPER',
      'PASSWORD_VALIDATIONS.PASSWORD_LOWER',
      'PASSWORD_VALIDATIONS.PASSWORD_SPECIAL',
      'PASSWORD_VALIDATIONS.PASSWORD_NUMBER',
    ]).subscribe((translations) => {
      resolve({
        [PasswordErrorKeys.passwordLength]: translations['PASSWORD_VALIDATIONS.PASSWORD_LENGTH'],
        [PasswordErrorKeys.passwordUpper]: translations['PASSWORD_VALIDATIONS.PASSWORD_UPPER'],
        [PasswordErrorKeys.passwordLower]: translations['PASSWORD_VALIDATIONS.PASSWORD_LOWER'],
        [PasswordErrorKeys.passwordSpecial]: translations['PASSWORD_VALIDATIONS.PASSWORD_SPECIAL'],
        [PasswordErrorKeys.passwordNumber]: translations['PASSWORD_VALIDATIONS.PASSWORD_NUMBER'],
      });
    });
  });
}

export abstract class PasswordValidatorsStatic {
  static PasswordMatcher(passwordControl: AbstractControl) {
    return (c: AbstractControl): { [key: string]: boolean } | null => {
      const confirmControl = c;

      if (passwordControl?.pristine || confirmControl?.pristine) {
        return null;
      }

      const password = passwordControl?.value as string;
      const confirm = confirmControl?.value as string;

      if (password.length > 0 && confirm.length > 0 && password === confirm) {
        return null;
      }
      return { [PasswordErrorKeys.passwordMatch]: true };
    };
  }

  static MinLength(minLength: number) {
    return (c: AbstractControl): { [key: string]: boolean } | null => {
      const passwordControl = c.get('password');

      if (passwordControl?.pristine) {
        return null;
      }

      if (passwordControl?.value.length >= minLength) {
        return null;
      }
      return { [PasswordErrorKeys.passwordLength]: true };
    };
  }

  static HasPattern =
  (searchPattern: string | RegExp, errorName: string) =>
  (c: AbstractControl) => {
    const passwordControl = c.get('password');
    if (passwordControl?.pristine) {
      return null;
    }
    const password = (passwordControl?.value as string) ?? '';
    if (password.search(searchPattern) >= 0) {
      return null;
    }
    return { [errorName]: true };
  };

  static HasUpper = (c: AbstractControl) => {
    return this.HasPattern(/[A-Z]/, PasswordErrorKeys.passwordUpper)(c);
  };

  static HasLower = (c: AbstractControl) => {
    return this.HasPattern(/[a-z]/, PasswordErrorKeys.passwordLower)(c);
  };

  static HasSpecial = (c: AbstractControl) => {
    return this.HasPattern(
      /[\^\$*.\[\]{}()?\-"!@#%&\/\\,<>':;|_~`+=]/,
      PasswordErrorKeys.passwordSpecial
    )(c);
  };

  static HasNumber = (c: AbstractControl) => {
    return this.HasPattern(/\d/, PasswordErrorKeys.passwordNumber)(c);
  };
}

/**
 * @returns all password validators
 * @see class PasswordValidatorsStatic
 */
export const PasswordValidators: ValidatorFn[] = [
  PasswordValidatorsStatic.MinLength(8),
  PasswordValidatorsStatic.HasUpper,
  PasswordValidatorsStatic.HasLower,
  PasswordValidatorsStatic.HasSpecial,
  PasswordValidatorsStatic.HasNumber,
];
